import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Button, Typography, TextField, InputAdornment, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Snackbar, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Stack, MenuItem, Select, FormControl, InputLabel, TableSortLabel,
  Switch, FormControlLabel, Divider, DialogContentText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import UndoIcon from '@mui/icons-material/Undo';
import { useNavigate, useLocation } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';
import { useAuthStore } from '../../store/authStore';
import ImportCsvDialog from './ImportCsvDialog';
import DescriptionField from './DescriptionField';

// Ceiling for free-text fields, matching the import dialog and the backend
// validators. See CLAUDE.md — not a DynamoDB constraint, just a sane bound.
const TEXT_FIELD_MAX = 4000;

// Roles permitted to unbuild (disassemble) a bundle — manager or greater.
const UNBUILD_ROLES = ['admin', 'manager', 'owner'];

// Roles permitted to bulk-import — must match `batchUpsert`'s allowedRoles.
const IMPORT_ROLES = ['admin', 'manager', 'owner'];

const UNIT_OPTIONS = [
  'piece', 'unit', 'each', 'pair', 'set', 'dozen',
  'oz', 'lb', 'g', 'kg', 'ton',
  'fl oz', 'cup', 'pt', 'qt', 'gal', 'ml', 'L',
  'in', 'ft', 'yd', 'm', 'cm', 'mm',
  'sq ft', 'sq m', 'sq yd',
  'cu ft', 'cu m', 'cu yd',
  'box', 'case', 'pack', 'pallet', 'roll', 'sheet', 'bag', 'bundle', 'carton',
];

const EMPTY_FORM = {
  name: '', sku: '', quantity: 0, unit: 'unit',
  category: '', warehouseId: '', costPrice: '', salePrice: '',
  // The stored description and the format it is stored in. The editor's format
  // tabs are a view over these and never write to them.
  description: '', descriptionFormat: 'plain', customUnit: '',
  vendorName: '', vendorId: '', vendorAddress: '', vendorPhone: '', vendorContact: '',
  serialNumber: '', lotNumber: '', expiryDate: '',
  type: 'standard', components: [], // bundle: components = [{ itemId, qtyPerBundle }]
  buildItemId: '', // bundle: standard item produced when the bundle is built
};

// Feature key to human-readable plan requirement
const FEATURE_LABELS = {
  'inventory.lot_number_tracking': 'Lot/Batch Tracking',
  'inventory.serial_number_tracking': 'Serial Number Tracking',
  'inventory.expiry_date_tracking': 'Expiry Date Tracking',
  'inventory.barcode_scanning': 'Barcode Scanning',
};

function FeatureGatedField({ featureKey, enabled, children }) {
  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      {!enabled && (
        <Tooltip title={`Requires "${FEATURE_LABELS[featureKey] || featureKey}" feature (paid plan)`}>
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1,
            cursor: 'not-allowed',
          }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isReadOnly } = useReadOnly();
  const storeRole = useAuthStore((s) => s.role);
  // The JWT-parsed store role can be null under Amplify v6, so prefer the live
  // role from GET /me (resolved server-side from the verified token).
  const [profileRole, setProfileRole] = useState(null);
  const canUnbuild = UNBUILD_ROLES.includes(profileRole || storeRole);
  const canImport = IMPORT_ROLES.includes(profileRole || storeRole);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cursor, setCursor] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Sorting
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Visibility toggles
  const [showInactive, setShowInactive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Features
  const [features, setFeatures] = useState({});
  const [categories, setCategories] = useState([]);

  // Create/Edit item dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  // Snapshot of the form as opened, so Save stays disabled until something
  // actually changes. Declared alongside `form` because the isFormDirty memo
  // below reads it during render.
  const [initialForm, setInitialForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Adjust dialog
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Delete confirmation dialog
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Build / Unbuild dialog
  const [buildTarget, setBuildTarget] = useState(null); // { item, unbuild }
  const [buildQty, setBuildQty] = useState('');
  const [building, setBuilding] = useState(false);

  // CSV import dialog
  const [importOpen, setImportOpen] = useState(false);

  // Category management dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [editingCat, setEditingCat] = useState(null);
  const [catSaving, setCatSaving] = useState(false);

  const lotEnabled = features['inventory.lot_number_tracking'] || false;
  const serialEnabled = features['inventory.serial_number_tracking'] || false;
  const expiryEnabled = features['inventory.expiry_date_tracking'] || false;
  const _barcodeEnabled = features['inventory.barcode_scanning'] || false;
  const bundleEnabled = features['inventory.bundle_products'] || false;
  const buildEnabled = features['inventory.bundle_assembly'] || false;
  const batchEnabled = features['inventory.batch_operations'] || false;

  // CSV import writes through POST /inventory/batch, so it needs the same
  // feature flag and roles that endpoint enforces server-side.
  const importDisabledReason = isReadOnly
    ? 'Your account is locked — imports are disabled.'
    : !batchEnabled
      ? 'Requires the Batch Operations feature.'
      : !canImport
        ? 'Only owners, admins, and managers can import inventory.'
        : null;

  // Standard (non-bundle) items that a bundle can link, excluding the item being edited.
  const linkableItems = useMemo(
    () => items.filter((i) => i.type !== 'bundle' && i.itemId !== editingItem?.itemId),
    [items, editingItem]
  );

  // A bundle's default cost = sum of (linked item cost × qty per bundle).
  // The Cost Price field overrides this when the user enters a value.
  const itemCostById = useMemo(() => {
    const m = {};
    for (const i of items) { m[i.itemId] = i.costPrice; }
    return m;
  }, [items]);
  const bundleCost = useMemo(() => {
    if (form.type !== 'bundle') { return null; }
    let sum = 0;
    let any = false;
    for (const c of form.components || []) {
      const cost = itemCostById[c.itemId];
      const qty = Number(c.qtyPerBundle);
      if (cost !== null && cost !== undefined && Number.isFinite(qty)) { sum += cost * qty; any = true; }
    }
    return any ? Math.round(sum * 100) / 100 : null;
  }, [form.type, form.components, itemCostById]);
  // Prefer the live client estimate; fall back to the server's authoritative
  // computed cost (set on the item when editing an existing bundle).
  const displayBundleCost = bundleCost ?? editingItem?.computedCost ?? null;

  // Save stays disabled until something actually changes. The description is
  // compared on its *saved* form, so merely browsing format tabs isn't a change.
  const isFormDirty = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  // Declared before the effect that calls it — a const arrow function is in the
  // temporal dead zone until this line runs, so ordering here is load-bearing.
  const loadCategories = async () => {
    try {
      const data = await tenantApi.listCategories();
      setCategories(data.categories || []);
    } catch { /* ignore */ }
  };

  // Load features and categories on mount
  useEffect(() => {
    tenantApi.getMyFeatures()
      .then(data => setFeatures(data.features || data || {}))
      .catch(() => {});
    tenantApi.getMyProfile()
      .then(data => setProfileRole(data.user?.role || null))
      .catch(() => {});
    loadCategories();
  }, []);

  const load = useCallback(async (reset = true, cursorVal = null) => {
    setLoading(true);
    setError(null);
    try {
      // `source` returns the description exactly as authored, which is what the
      // edit dialog must load; the table renders `descriptionText` instead.
      const params = { limit: 50, descriptionFormat: 'source' };
      if (search) { params.search = search; }
      if (categoryFilter) { params.category = categoryFilter; }
      if (showInactive) { params.showInactive = 'true'; }
      if (showDeleted) { params.showDeleted = 'true'; }
      if (!reset && cursorVal) { params.cursor = cursorVal; }
      const data = await tenantApi.listInventory(params);
      setItems(reset ? data.items : (prev) => [...prev, ...data.items]);
      setCursor(data.cursor || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, showInactive, showDeleted]);

  useEffect(() => { load(true); }, [load]);

  // Client-side sort
  const sortedItems = useMemo(() => {
    if (!sortBy) { return items; }
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...items].sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (aVal === null || aVal === undefined) { aVal = ''; }
      if (bVal === null || bVal === undefined) { bVal = ''; }
      if (typeof aVal === 'number' && typeof bVal === 'number') { return (aVal - bVal) * dir; }
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [items, sortBy, sortDir]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // Unique categories from loaded items + saved categories
  const allCategories = useMemo(() => {
    const fromItems = new Set(items.map(i => i.category).filter(Boolean));
    categories.forEach(c => fromItems.add(c.name));
    return [...fromItems].sort();
  }, [items, categories]);

  // Every category the tenant has — managed (CAT# record, with description/id)
  // and item-only (a string used on items but never formally created). Both are
  // editable/deletable; 'Uncategorized' is the protected fallback and excluded.
  const manageableCategories = useMemo(() => {
    const byName = new Map();
    categories.forEach((c) => byName.set(c.name, { name: c.name, categoryId: c.categoryId, description: c.description || '' }));
    allCategories.forEach((name) => {
      if (!byName.has(name)) { byName.set(name, { name, categoryId: null, description: '' }); }
    });
    byName.delete('Uncategorized');
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, allCategories]);

  // Merge standard units with custom units found in existing items
  const allUnitOptions = useMemo(() => {
    const standard = new Set(UNIT_OPTIONS);
    const custom = items
      .map(i => i.unit)
      .filter(u => u && !standard.has(u));
    const uniqueCustom = [...new Set(custom)].sort();
    return { standard: UNIT_OPTIONS, custom: uniqueCustom };
  }, [items]);

  // ─── Create / Edit ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setInitialForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    const isCustomUnit = !UNIT_OPTIONS.includes(item.unit) && !allUnitOptions.custom.includes(item.unit);
    const loaded = {
      name: item.name || '',
      sku: item.sku || '',
      quantity: item.quantity || 0,
      unit: isCustomUnit ? '__custom__' : (item.unit || 'unit'),
      customUnit: isCustomUnit ? item.unit : '',
      category: item.category || '',
      warehouseId: item.warehouseId || '',
      // Bundles with auto cost (costIsAuto) keep the field blank so it stays auto.
      costPrice: (item.type === 'bundle' && item.costIsAuto) ? '' : (item.costPrice ?? ''),
      salePrice: item.salePrice ?? '',
      description: item.description || '',
      // The list request asks for `source`, so this is the text as authored.
      descriptionFormat: item.descriptionFormat || 'plain',
      vendorName: item.vendorName || '',
      vendorId: item.vendorId || '',
      vendorAddress: item.vendorAddress || '',
      vendorPhone: item.vendorPhone || '',
      vendorContact: item.vendorContact || '',
      serialNumber: item.serialNumber || '',
      lotNumber: item.lotNumber || '',
      expiryDate: item.expiryDate || '',
      type: item.type || 'standard',
      components: Array.isArray(item.components)
        ? item.components.map((c) => ({ itemId: c.itemId, qtyPerBundle: c.qtyPerBundle }))
        : [],
      buildItemId: item.buildItemId || '',
    };
    setForm(loaded);
    setInitialForm(loaded);
    setDialogOpen(true);
  };

  // Open the edit dialog when navigated here from the item detail page's Edit icon.
  useEffect(() => {
    const editId = location.state?.editItemId;
    if (!editId || items.length === 0) { return; }
    const target = items.find((i) => i.itemId === editId);
    if (target) { openEdit(target); }
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, location.state]);

  const handleSave = async () => {
    setSaving(true);
    const unit = form.unit === '__custom__' ? form.customUnit : form.unit;
    const isBundle = form.type === 'bundle';
    const payload = {
      name: form.name,
      sku: form.sku,
      unit,
      category: form.category || 'Uncategorized',
      warehouseId: form.warehouseId || 'default',
      // Store the description in the format the user ticked, converting from
      // whichever tab they happened to be editing in.
      description: form.description || null,
      descriptionFormat: form.descriptionFormat || 'plain',
      // Blank Cost Price stores null. For bundles, null = "auto" — the API
      // computes the cost from linked items on read. A value is an override.
      costPrice: form.costPrice !== '' ? parseFloat(form.costPrice) : null,
      salePrice: form.salePrice !== '' ? parseFloat(form.salePrice) : null,
    };
    // Bundle: stock is computed from linked items; send components, never quantity.
    if (isBundle) {
      payload.components = (form.components || [])
        .filter((c) => c.itemId && Number(c.qtyPerBundle) > 0)
        .map((c) => ({ itemId: c.itemId, qtyPerBundle: Number(c.qtyPerBundle) }));
      // Build Item: the standard item produced when the bundle is built (null clears).
      payload.buildItemId = form.buildItemId || null;
    } else if (!editingItem) {
      payload.quantity = parseFloat(form.quantity) || 0;
    }
    if (!editingItem) { payload.type = isBundle ? 'bundle' : 'standard'; }

    // Feature-gated fields (always send if user filled them — backend validates)
    if (form.serialNumber) { payload.serialNumber = form.serialNumber; }
    if (form.lotNumber) { payload.lotNumber = form.lotNumber; }
    if (form.expiryDate) { payload.expiryDate = form.expiryDate; }
    if (form.vendorName) { payload.vendorName = form.vendorName; }
    if (form.vendorId) { payload.vendorId = form.vendorId; }
    if (form.vendorAddress) { payload.vendorAddress = form.vendorAddress; }
    if (form.vendorPhone) { payload.vendorPhone = form.vendorPhone; }
    if (form.vendorContact) { payload.vendorContact = form.vendorContact; }

    try {
      if (editingItem) {
        await tenantApi.updateInventoryItem(editingItem.itemId, payload);
        setToast({ open: true, message: 'Item updated.', severity: 'success' });
      } else {
        await tenantApi.createInventoryItem(payload);
        setToast({ open: true, message: 'Item created.', severity: 'success' });
      }
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      setEditingItem(null);
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Adjust ────────────────────────────────────────────────────────────────

  const handleAdjust = async () => {
    const delta = parseFloat(adjustDelta);
    if (isNaN(delta) || delta === 0) { return; }
    setAdjusting(true);
    try {
      await tenantApi.adjustInventory(adjustItem.itemId, delta, adjustReason);
      setToast({ open: true, message: `Stock adjusted by ${delta > 0 ? '+' : ''}${delta}.`, severity: 'success' });
      setAdjustItem(null);
      setAdjustDelta('');
      setAdjustReason('');
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setAdjusting(false);
    }
  };

  // ─── Build / Unbuild ─────────────────────────────────────────────────────────

  // Resolve a build item's display name (for the dialog) from loaded items.
  const itemNameById = useMemo(() => {
    const m = {};
    for (const i of items) { m[i.itemId] = i.name; }
    return m;
  }, [items]);

  const handleBuild = async () => {
    const qty = parseInt(buildQty, 10);
    if (!Number.isInteger(qty) || qty <= 0) { return; }
    const { item, unbuild } = buildTarget;
    setBuilding(true);
    try {
      await tenantApi.buildBundle(item.itemId, qty, { unbuild });
      setToast({
        open: true,
        message: unbuild ? `Unbuilt ${qty} × ${item.name}.` : `Built ${qty} × ${item.name}.`,
        severity: 'success',
      });
      setBuildTarget(null);
      setBuildQty('');
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setBuilding(false);
    }
  };

  // ─── Delete / Status ───────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await tenantApi.deleteInventoryItem(deleteItem.itemId);
      setToast({ open: true, message: 'Item marked for deletion (7-day hold).', severity: 'success' });
      setDeleteItem(null);
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await tenantApi.updateItemStatus(item.itemId, newStatus);
      setToast({ open: true, message: `Item ${newStatus === 'active' ? 'activated' : newStatus === 'inactive' ? 'deactivated' : 'marked for deletion'}.`, severity: 'success' });
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    }
  };

  // ─── Category Management ───────────────────────────────────────────────────

  const handleCatSave = async () => {
    setCatSaving(true);
    try {
      if (editingCat) {
        // Managed categories use their CAT# id; item-only categories are
        // addressed by their (old) name — the backend cascades either way.
        const ref = editingCat.categoryId || editingCat.name;
        await tenantApi.updateCategory(ref, catForm);
        setToast({ open: true, message: 'Category updated for all items.', severity: 'success' });
      } else {
        await tenantApi.createCategory(catForm);
        setToast({ open: true, message: 'Category created.', severity: 'success' });
      }
      setCatForm({ name: '', description: '' });
      setEditingCat(null);
      loadCategories();
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    }
    setCatSaving(false);
  };

  const handleCatDelete = async (cat) => {
    try {
      await tenantApi.deleteCategory(cat.categoryId || cat.name);
      setToast({ open: true, message: `Category "${cat.name}" deleted. Items moved to Uncategorized.`, severity: 'success' });
      loadCategories();
      load(true);
    } catch (e) {
      setToast({ open: true, message: e.message, severity: 'error' });
    }
  };

  const stockColor = (qty) => {
    if (qty <= 0) { return 'error'; }
    if (qty <= 10) { return 'warning'; }
    return 'default';
  };

  const statusChip = (item) => {
    const s = item.status || 'active';
    if (s === 'inactive') { return <Chip label="Inactive" size="small" color="warning" variant="outlined" />; }
    if (s === 'pending_deletion') { return <Chip label="Pending Delete" size="small" color="error" variant="outlined" />; }
    return null;
  };

  const isLocked = (item) => {
    const s = item.status || 'active';
    return s === 'inactive' || s === 'pending_deletion';
  };

  // Sortable header cell
  const SortCell = ({ field, label, align, width }) => (
    <TableCell align={align} sx={{ width }}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Inventory</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<CategoryIcon />} onClick={() => setCatDialogOpen(true)} disabled={isReadOnly}>
            Categories
          </Button>
          <Tooltip title={importDisabledReason || 'Bulk-create or update items from a CSV file'}>
            <span>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                disabled={Boolean(importDisabledReason)}
                onClick={() => setImportOpen(true)}
              >
                Import CSV
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={isReadOnly}>
            Add
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Search all fields..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Category"
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {allCategories.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label={<Typography variant="body2">Show Inactive</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />}
          label={<Typography variant="body2">Show Deleted</Typography>}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Qty legend */}
      <Stack
        direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
        mb={1} sx={{ color: 'text.secondary' }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Qty:</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="0" color="error" size="small" sx={{ height: 18 }} />
          <Typography variant="caption">Out of stock</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="≤10" color="warning" size="small" sx={{ height: 18 }} />
          <Typography variant="caption">Low stock</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="11+" size="small" sx={{ height: 18 }} />
          <Typography variant="caption">In stock</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip label="N" size="small" variant="outlined" sx={{ height: 18 }} />
          <Typography variant="caption">Bundle (computed from linked items)</Typography>
        </Stack>
      </Stack>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1150 }}>
          <TableHead>
            <TableRow>
              <SortCell field="sku" label="SKU" width="9%" />
              <SortCell field="name" label="Name" width="13%" />
              <SortCell field="type" label="Type" width="6%" />
              <SortCell field="description" label="Description" width="11%" />
              <SortCell field="category" label="Category" width="9%" />
              <SortCell field="quantity" label="Qty" align="right" width="7%" />
              <SortCell field="unit" label="Unit" width="5%" />
              <SortCell field="warehouseId" label="Warehouse" width="8%" />
              <SortCell field="costPrice" label="Cost" align="right" width="6%" />
              <SortCell field="salePrice" label="Sale Price" align="right" width="7%" />
              <TableCell sx={{ width: '6%' }}>Status</TableCell>
              <TableCell sx={{ width: '13%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No items found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedItems.map((item) => (
                <TableRow
                  key={item.itemId}
                  hover
                  sx={{
                    opacity: isLocked(item) ? 0.6 : 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/inventory/${item.itemId}`)}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{item.sku}</TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}
                  </TableCell>
                  <TableCell>
                    {item.type === 'bundle' ? (
                      <Tooltip title={`Bundle of ${(item.components || []).length} linked item(s) — stock is computed`}>
                        <Chip label="Bundle" size="small" color="secondary" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip label="Item" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {/* Flattened text, so markup never leaks into the table. */}
                    {item.descriptionText || item.description || '—'}
                  </TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="right">
                    {item.type === 'bundle' ? (
                      <Tooltip title="Computed from linked items">
                        <Chip label={item.quantity} color={stockColor(item.quantity)} size="small" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip label={item.quantity} color={stockColor(item.quantity)} size="small" />
                    )}
                  </TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{item.warehouseId}</TableCell>
                  <TableCell align="right">{item.costPrice !== null && item.costPrice !== undefined ? `$${Number(item.costPrice).toFixed(2)}` : '—'}</TableCell>
                  <TableCell align="right">{item.salePrice !== null && item.salePrice !== undefined ? `$${Number(item.salePrice).toFixed(2)}` : '—'}</TableCell>
                  <TableCell>{statusChip(item)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0}>
                      {/* Active items: edit, adjust/build, deactivate, delete */}
                      {(item.status || 'active') === 'active' && (
                        <>
                          {/* Bundle items can't be edited while the Inventory Bundles
                              feature is off — only inactivate/delete remain. */}
                          <Tooltip title={item.type === 'bundle' && !bundleEnabled
                            ? 'Enable the Inventory Bundles add-on to edit this bundle.'
                            : 'Edit item'}>
                            <span>
                              <IconButton size="small" onClick={() => openEdit(item)}
                                disabled={isReadOnly || (item.type === 'bundle' && !bundleEnabled)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* Standard items: adjust stock. Bundles: build/unbuild (stock is computed). */}
                          {item.type !== 'bundle' && (
                            <Tooltip title="Adjust stock">
                              <IconButton size="small" onClick={() => { setAdjustItem(item); setAdjustDelta(''); setAdjustReason(''); }} disabled={isReadOnly}>
                                <EditIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Build/Unbuild: shown when the feature is on OR a build item
                              is configured; greyed with a message when the feature is off. */}
                          {item.type === 'bundle' && (buildEnabled || item.buildItemId) && (
                            <Tooltip title={!buildEnabled
                              ? 'Enable the Build Bundles feature to build bundles.'
                              : (item.buildItemId ? 'Build (assemble into build item)' : 'Set a Build Item on this bundle to enable building')}>
                              <span>
                                <IconButton size="small" color="success"
                                  onClick={() => { setBuildTarget({ item, unbuild: false }); setBuildQty(''); }}
                                  disabled={isReadOnly || !buildEnabled || !item.buildItemId}>
                                  <BuildIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {item.type === 'bundle' && canUnbuild && (buildEnabled || item.buildItemId) && (
                            <Tooltip title={!buildEnabled
                              ? 'Enable the Build Bundles feature to unbuild bundles.'
                              : (item.buildItemId ? 'Unbuild (disassemble back into components)' : 'Set a Build Item on this bundle to enable unbuilding')}>
                              <span>
                                <IconButton size="small" color="warning"
                                  onClick={() => { setBuildTarget({ item, unbuild: true }); setBuildQty(''); }}
                                  disabled={isReadOnly || !buildEnabled || !item.buildItemId}>
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Mark inactive">
                            <IconButton size="small" onClick={() => handleStatusChange(item, 'inactive')} disabled={isReadOnly}>
                              <BlockIcon fontSize="small" color="warning" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark for deletion">
                            <IconButton size="small" onClick={() => setDeleteItem(item)} disabled={isReadOnly}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Inactive items: activate, delete */}
                      {item.status === 'inactive' && (
                        <>
                          <Tooltip title="Activate">
                            <IconButton size="small" onClick={() => handleStatusChange(item, 'active')} disabled={isReadOnly}>
                              <CheckCircleIcon fontSize="small" color="success" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark for deletion">
                            <IconButton size="small" onClick={() => setDeleteItem(item)} disabled={isReadOnly}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Pending deletion items: cancel deletion only */}
                      {item.status === 'pending_deletion' && (
                        <Tooltip title="Cancel deletion">
                          <IconButton size="small" onClick={() => handleStatusChange(item, 'active')} disabled={isReadOnly}>
                            <RestoreIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {cursor && (
        <Box mt={2} display="flex" justifyContent="center">
          <Button onClick={() => load(false, cursor)} disabled={loading}>Load More</Button>
        </Box>
      )}

      {/* Create / Edit Item Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Item type — standard vs bundle */}
            <FormControl size="small" fullWidth>
              <InputLabel>Item Type</InputLabel>
              <Select
                value={form.type}
                label="Item Type"
                disabled={Boolean(editingItem)}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <MenuItem value="standard">Item (standard stock)</MenuItem>
                <MenuItem value="bundle" disabled={!bundleEnabled}>
                  Bundle (dynamic stock){!bundleEnabled ? ' — requires Inventory Bundles add-on' : ''}
                </MenuItem>
              </Select>
            </FormControl>

            {/* Core fields */}
            <Stack direction="row" spacing={2}>
              <TextField label="Name *" size="small" fullWidth value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <TextField label="SKU *" size="small" fullWidth value={form.sku}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
            </Stack>

            <DescriptionField
              value={form.description}
              format={form.descriptionFormat}
              maxLength={TEXT_FIELD_MAX}
              disabled={isReadOnly}
              original={editingItem
                ? { text: editingItem.description || '', format: editingItem.descriptionFormat || 'plain' }
                : null}
              onChange={(description, descriptionFormat) =>
                setForm((f) => ({ ...f, description, descriptionFormat }))}
            />

            <Stack direction="row" spacing={2}>
              {/* Category dropdown */}
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  label="Category"
                >
                  {allCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="__new__" sx={{ fontStyle: 'italic' }}>+ New Category...</MenuItem>
                </Select>
              </FormControl>
              {form.category === '__new__' && (
                <TextField label="New Category Name" size="small" fullWidth
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value === '' ? '__new__' : e.target.value }))}
                  autoFocus />
              )}
              <TextField label="Warehouse ID" size="small" fullWidth value={form.warehouseId}
                onChange={(e) => setForm((f) => ({ ...f, warehouseId: e.target.value }))} />
            </Stack>

            <Stack direction="row" spacing={2}>
              {!editingItem && form.type !== 'bundle' && (
                <TextField label="Quantity" size="small" type="number" fullWidth value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
              )}
              {/* Unit dropdown */}
              <FormControl size="small" fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  label="Unit"
                >
                  {allUnitOptions.standard.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                  {allUnitOptions.custom.length > 0 && <Divider />}
                  {allUnitOptions.custom.map((u) => (
                    <MenuItem key={`custom-${u}`} value={u}>{u}</MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="__custom__" sx={{ fontStyle: 'italic' }}>Custom...</MenuItem>
                </Select>
              </FormControl>
              {form.unit === '__custom__' && (
                <TextField label="Custom Unit" size="small" fullWidth value={form.customUnit}
                  onChange={(e) => setForm((f) => ({ ...f, customUnit: e.target.value }))} autoFocus />
              )}
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField label="Cost Price" size="small" type="number" fullWidth value={form.costPrice}
                onChange={(e) => setForm((f) => ({ ...f, costPrice: e.target.value }))}
                placeholder={form.type === 'bundle' && displayBundleCost !== null ? String(displayBundleCost) : undefined}
                InputLabelProps={form.type === 'bundle' && displayBundleCost !== null ? { shrink: true } : undefined}
                helperText={form.type === 'bundle'
                  ? (displayBundleCost !== null
                    ? `Auto-calculated from linked items: $${displayBundleCost.toFixed(2)}. Leave blank to keep it automatic, or enter a value to override.`
                    : 'Cost is auto-calculated from linked items. Enter a value to override.')
                  : undefined} />
              <TextField label="Sale Price" size="small" type="number" fullWidth value={form.salePrice}
                onChange={(e) => setForm((f) => ({ ...f, salePrice: e.target.value }))} />
            </Stack>

            {/* Bundle components — each linked item with a per-bundle stock value */}
            {form.type === 'bundle' && (
              <>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Bundle Components</Typography>
                  {editingItem && typeof editingItem.computedStock === 'number' && (
                    <Chip size="small" color="info" label={`Available: ${editingItem.computedStock} buildable`} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Link inventory items and set how many of each are consumed per bundle. Stock is the number of
                  complete bundles you can build.
                </Typography>
                {(form.components || []).map((c, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Linked item</InputLabel>
                      <Select
                        value={c.itemId}
                        label="Linked item"
                        onChange={(e) => setForm((f) => {
                          const next = [...f.components];
                          next[idx] = { ...next[idx], itemId: e.target.value };
                          return { ...f, components: next };
                        })}
                      >
                        {linkableItems.map((li) => (
                          <MenuItem key={li.itemId} value={li.itemId}>
                            {li.name} ({li.sku}) — {li.quantity} on hand
                          </MenuItem>
                        ))}
                        {/* Preserve a previously-linked item not on the current page */}
                        {c.itemId && !linkableItems.some((li) => li.itemId === c.itemId) && (
                          <MenuItem value={c.itemId}>{c.itemId}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Qty / bundle" size="small" type="number" sx={{ width: 130 }}
                      value={c.qtyPerBundle}
                      onChange={(e) => setForm((f) => {
                        const next = [...f.components];
                        next[idx] = { ...next[idx], qtyPerBundle: e.target.value };
                        return { ...f, components: next };
                      })}
                    />
                    <IconButton size="small" color="error"
                      onClick={() => setForm((f) => ({ ...f, components: f.components.filter((_, i) => i !== idx) }))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setForm((f) => ({ ...f, components: [...(f.components || []), { itemId: '', qtyPerBundle: 1 }] }))}>
                  Add component
                </Button>

                {/* Build Item — the standard item produced when this bundle is built */}
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">Build Item</Typography>
                  {!buildEnabled && (
                    <Chip label="Requires Build Bundles add-on" size="small" variant="outlined" color="info" />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Optional. The standard item produced when you Build this bundle. Building consumes the
                  components above and increments this item; Unbuild reverses it.
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Build Item</InputLabel>
                  <Select
                    value={form.buildItemId}
                    label="Build Item"
                    onChange={(e) => setForm((f) => ({ ...f, buildItemId: e.target.value }))}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {linkableItems.map((li) => (
                      <MenuItem key={li.itemId} value={li.itemId}>
                        {li.name} ({li.sku})
                      </MenuItem>
                    ))}
                    {/* Preserve a previously-set build item not on the current page */}
                    {form.buildItemId && !linkableItems.some((li) => li.itemId === form.buildItemId) && (
                      <MenuItem value={form.buildItemId}>{itemNameById[form.buildItemId] || form.buildItemId}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </>
            )}

            {/* Feature-gated fields — always shown, disabled if feature off */}
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Advanced Tracking Fields</Typography>

            <FeatureGatedField featureKey="inventory.serial_number_tracking" enabled={serialEnabled}>
              <TextField label="Serial Number" size="small" fullWidth value={form.serialNumber}
                disabled={!serialEnabled}
                onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
                helperText={!serialEnabled ? 'Requires Serial Number Tracking (paid plan)' : ''} />
            </FeatureGatedField>

            <FeatureGatedField featureKey="inventory.lot_number_tracking" enabled={lotEnabled}>
              <TextField label="Lot/Batch Number" size="small" fullWidth value={form.lotNumber}
                disabled={!lotEnabled}
                onChange={(e) => setForm((f) => ({ ...f, lotNumber: e.target.value }))}
                helperText={!lotEnabled ? 'Requires Lot/Batch Tracking (paid plan)' : ''} />
            </FeatureGatedField>

            <FeatureGatedField featureKey="inventory.expiry_date_tracking" enabled={expiryEnabled}>
              <TextField label="Expiry Date" size="small" fullWidth type="date" value={form.expiryDate}
                disabled={!expiryEnabled}
                onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText={!expiryEnabled ? 'Requires Expiry Date Tracking (paid plan)' : ''} />
            </FeatureGatedField>

            {/* Vendor fields — gated behind lot tracking */}
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Vendor Information
              {!lotEnabled && (
                <Chip label="Requires Lot/Batch Tracking" size="small" variant="outlined" color="info" sx={{ ml: 1 }} />
              )}
            </Typography>

            <FeatureGatedField featureKey="inventory.lot_number_tracking" enabled={lotEnabled}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField label="Vendor Name" size="small" fullWidth value={form.vendorName}
                    disabled={!lotEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))} />
                  <TextField label="Vendor ID" size="small" fullWidth value={form.vendorId}
                    disabled={!lotEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, vendorId: e.target.value }))} />
                </Stack>
                <TextField label="Vendor Address" size="small" fullWidth value={form.vendorAddress}
                  disabled={!lotEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, vendorAddress: e.target.value }))} />
                <Stack direction="row" spacing={2}>
                  <TextField label="Vendor Phone" size="small" fullWidth value={form.vendorPhone}
                    disabled={!lotEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, vendorPhone: e.target.value }))} />
                  <TextField label="Vendor Contact" size="small" fullWidth value={form.vendorContact}
                    disabled={!lotEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, vendorContact: e.target.value }))} />
                </Stack>
              </Stack>
            </FeatureGatedField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name || !form.sku || !isFormDirty}
          >
            {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust Dialog */}
      <Dialog open={!!adjustItem} onClose={() => setAdjustItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Stock - {adjustItem?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current quantity: <strong>{adjustItem?.quantity}</strong>
            </Typography>
            <TextField
              label="Delta (+ to add, - to remove)"
              size="small" type="number" fullWidth
              value={adjustDelta}
              onChange={(e) => setAdjustDelta(e.target.value)}
              helperText={adjustDelta ? `New quantity: ${adjustItem?.quantity + parseFloat(adjustDelta || 0)}` : ''}
            />
            <TextField
              label="Reason"
              size="small" fullWidth multiline rows={2}
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="e.g. Received PO-12345"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustItem(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdjust}
            disabled={adjusting || !adjustDelta || parseFloat(adjustDelta) === 0}
          >
            {adjusting ? 'Saving...' : 'Apply Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Build / Unbuild Dialog */}
      <Dialog open={!!buildTarget} onClose={() => setBuildTarget(null)} maxWidth="xs" fullWidth>
        {buildTarget && (() => {
          const { item, unbuild } = buildTarget;
          const buildItemObj = items.find((i) => i.itemId === item.buildItemId);
          const buildItemName = buildItemObj?.name || itemNameById[item.buildItemId] || item.buildItemId;
          const buildable = typeof item.computedStock === 'number' ? item.computedStock : item.quantity;
          const qtyNum = parseInt(buildQty, 10);
          const max = unbuild
            ? (buildItemObj ? buildItemObj.quantity : undefined)
            : buildable;
          const exceedsMax = Number.isInteger(qtyNum) && max !== undefined && qtyNum > max;
          return (
            <>
              <DialogTitle>{unbuild ? 'Unbuild' : 'Build'} — {item.name}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Build Item: <strong>{buildItemName}</strong>
                    {buildItemObj && <> (currently {buildItemObj.quantity} on hand)</>}
                  </Typography>
                  {!unbuild ? (
                    <Alert severity="info">
                      Building consumes the linked components and adds the built quantity to{' '}
                      <strong>{buildItemName}</strong>. You can build up to <strong>{buildable}</strong> right now.
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      Unbuilding removes the quantity from <strong>{buildItemName}</strong> and restores the
                      linked components. Manager access required.
                    </Alert>
                  )}
                  <TextField
                    label={`Quantity to ${unbuild ? 'unbuild' : 'build'}`}
                    size="small" type="number" fullWidth autoFocus
                    value={buildQty}
                    onChange={(e) => setBuildQty(e.target.value)}
                    error={exceedsMax}
                    helperText={exceedsMax
                      ? (unbuild ? `Only ${max} built unit(s) available to unbuild.` : `Only ${max} buildable from current component stock.`)
                      : ' '}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBuildTarget(null)}>Cancel</Button>
                <Button
                  variant="contained"
                  color={unbuild ? 'warning' : 'success'}
                  onClick={handleBuild}
                  disabled={building || !Number.isInteger(qtyNum) || qtyNum <= 0 || exceedsMax}
                >
                  {building ? 'Working...' : unbuild ? 'Unbuild' : 'Build'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)} maxWidth="sm">
        <DialogTitle>Mark Item for Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteItem?.name}</strong> (SKU: {deleteItem?.sku}) will be marked for deletion and permanently removed after 7 days.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This item may be referenced in reports, external integrations, or historical records. Deleting it could affect data integrity in those systems. You can cancel the deletion within the 7-day window.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteItem(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Processing...' : 'Mark for Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Categories</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Renaming a category updates every inventory item using it. Deleting one moves its items to “Uncategorized”.
            </Typography>
            {/* All categories — managed and item-derived */}
            {manageableCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No categories yet.</Typography>
            ) : (
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manageableCategories.map((cat) => (
                      <TableRow key={cat.categoryId || cat.name} hover selected={editingCat?.name === cat.name}>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cat.description || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Rename (updates all items)">
                            <IconButton size="small" onClick={() => {
                              setEditingCat(cat);
                              setCatForm({ name: cat.name, description: cat.description || '' });
                            }} disabled={isReadOnly}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete (items → Uncategorized)">
                            <IconButton size="small" onClick={() => handleCatDelete(cat)} disabled={isReadOnly}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            <Divider />
            <Typography variant="subtitle2">{editingCat ? `Rename “${editingCat.name}”` : 'Add Category'}</Typography>
            <TextField label="Category Name *" size="small" fullWidth value={catForm.name}
              onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Description" size="small" fullWidth value={catForm.description}
              onChange={(e) => setCatForm((f) => ({ ...f, description: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          {editingCat && (
            <Button onClick={() => { setEditingCat(null); setCatForm({ name: '', description: '' }); }}>
              Cancel Edit
            </Button>
          )}
          <Button onClick={() => setCatDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleCatSave} disabled={catSaving || !catForm.name}>
            {catSaving ? 'Saving...' : editingCat ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV import — standard items only; bundles are created individually. */}
      <ImportCsvDialog
        open={importOpen}
        features={features}
        onImported={() => {
          load(true);
          loadCategories();
          setToast({ open: true, message: 'Inventory imported.', severity: 'success' });
        }}
        onClose={() => setImportOpen(false)}
      />

      <Snackbar
        open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
